import React, { useEffect, useRef, useState } from 'react';
import { wire } from 'react-hot-wire';
import { FiChevronDown } from 'react-icons/fi';
import { injectIntl } from 'react-intl';
import { useOnClickOutside } from 'usehooks-ts';

const BUTTON_ID = 'language-menu-button';
const LIST_ID = 'language-menu-list';

function LanguageMenu({ intl, 'services.i18nService': i18nService }) {
	const [isOpen, setIsOpen] = useState(false);
	const focusOnOpen = useRef('first');
	const rootRef = useRef(null);
	const buttonRef = useRef(null);
	const listRef = useRef(null);
	const currentLocale = i18nService.locale();
	const currentCode = currentLocale.toUpperCase();

	const close = () => setIsOpen(false);
	const getRows = () => Array.from(listRef.current.querySelectorAll('[role="menuitem"]'));

	useOnClickOutside(rootRef, close);
	useOnClickOutside(rootRef, close, 'touchstart');

	useEffect(() => {
		if (isOpen) {
			const rows = getRows();

			rows[focusOnOpen.current === 'last' ? rows.length - 1 : 0].focus();
		}
	}, [isOpen]);

	const open = target => {
		focusOnOpen.current = target;
		setIsOpen(true);
	};

	const handleButtonKeyDown = event => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			open(event.key === 'ArrowUp' ? 'last' : 'first');
		}
	};

	const handleListKeyDown = event => {
		const rows = getRows();
		const index = rows.indexOf(document.activeElement);

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				rows[(index + 1) % rows.length].focus();
				break;
			case 'ArrowUp':
				event.preventDefault();
				rows[(index - 1 + rows.length) % rows.length].focus();
				break;
			case 'Home':
				event.preventDefault();
				rows[0].focus();
				break;
			case 'End':
				event.preventDefault();
				rows[rows.length - 1].focus();
				break;
			case 'Escape':
				event.preventDefault();
				buttonRef.current.focus();
				close();
				break;
			case 'Tab':
				// The focused row unmounts in this handler, so without this the default Tab starts from <body>.
				buttonRef.current.focus();
				close();
				break;
			case 'Enter':
			case ' ':
				// A key held down since opening the menu must not pick the first row.
				if (event.repeat) {
					event.preventDefault();
				}
				break;
			default:
		}
	};

	const handleSelect = locale => {
		buttonRef.current.focus();
		close();
		i18nService.changeLocale(locale);
	};

	return (
		<div ref={rootRef} className="languages">
			<button
				ref={buttonRef}
				id={BUTTON_ID}
				type="button"
				className="languages-button"
				aria-haspopup="menu"
				aria-expanded={isOpen}
				aria-controls={isOpen ? LIST_ID : undefined}
				aria-label={`${intl.formatMessage({ id: 'header.title.language' })}: ${currentCode}`}
				onClick={() => (isOpen ? close() : open('first'))}
				onKeyDown={handleButtonKeyDown}
			>
				{currentCode}
				<FiChevronDown />
			</button>
			{isOpen ? (
				<div
					ref={listRef}
					id={LIST_ID}
					role="menu"
					aria-labelledby={BUTTON_ID}
					className="languages-list"
					onKeyDown={handleListKeyDown}
				>
					{i18nService.availableLocales().map(locale => {
						const code = locale.toUpperCase();

						return (
							<button
								key={locale}
								type="button"
								role="menuitem"
								tabIndex={-1}
								lang={locale}
								className="languages-item"
								aria-label={`${code} – ${intl.formatMessage({ id: `locale.${locale}` })}`}
								aria-current={locale === currentLocale ? 'true' : undefined}
								onClick={() => handleSelect(locale)}
								onMouseEnter={event => event.currentTarget.focus()}
							>
								{code}
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}

export default injectIntl(wire(['services.i18nService'], LanguageMenu));
